// NatureLandscapeGallery.js - Nature Landscape Portfolio Gallery component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a nature landscape portfolio gallery with image grid and organic styling
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the gallery is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The gallery elements
 */
export const createNatureLandscapeGalleryElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const gallerySectionClass = 'nature-gallery-section';
  const galleryContainerClass = 'nature-gallery-container';
  const galleryHeaderClass = 'nature-gallery-header';
  const galleryTitleClass = 'nature-gallery-title';
  const gallerySubtitleClass = 'nature-gallery-subtitle';
  const galleryGridClass = 'nature-gallery-grid';
  const galleryImageItemClass = 'nature-gallery-image-item';
  const galleryImageClass = 'nature-gallery-image';
  const galleryImageOverlayClass = 'nature-gallery-image-overlay';
  const galleryImageInfoClass = 'nature-gallery-image-info';
  const galleryImageTitleClass = 'nature-gallery-image-title';
  const galleryImageLocationClass = 'nature-gallery-image-location';
  const galleryPatternOverlayClass = 'nature-gallery-pattern-overlay';

  // Desktop Styles - Nature theme with organic design
  const gallerySectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f0f4f0',
    background: `
      radial-gradient(circle at 25% 25%, rgba(34, 197, 94, 0.06) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.04) 0%, transparent 50%),
      linear-gradient(135deg, #f0f4f0 0%, #e8f5e8 25%, #d1f2d1 50%, #e8f5e8 75%, #f0f4f0 100%)
    `,
    color: '#1f2937',
    width: '100%',
    overflow: 'hidden'
  };

  const galleryPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 25% 25%, rgba(34, 197, 94, 0.02) 1px, transparent 1px),
      radial-gradient(circle at 75% 75%, rgba(59, 130, 246, 0.02) 1px, transparent 1px)
    `,
    backgroundSize: '100px 100px, 140px 140px',
    backgroundPosition: '0 0, 70px 70px',
    zIndex: '0',
    opacity: '0.4'
  };

  const galleryContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const galleryHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const galleryTitleStyles = {
    fontSize: '56px',
    fontWeight: '300',
    color: '#1f2937',
    marginBottom: '24px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.1',
    position: 'relative'
  };

  const gallerySubtitleStyles = {
    fontSize: '20px',
    color: 'rgba(31, 41, 55, 0.8)',
    maxWidth: '700px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const galleryGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '30px',
    marginTop: '40px'
  };

  const galleryImageItemStyles = {
    position: 'relative',
    height: '460px',
    borderRadius: '20px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    background: 'rgba(255, 255, 255, 0.8)',
    border: '1px solid rgba(34, 197, 94, 0.15)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)'
  };

  const galleryImageItemHoverStyles = {
    transform: 'translateY(-15px) scale(1.03)',
    border: '1px solid rgba(34, 197, 94, 0.3)',
    boxShadow: `
      0 30px 60px rgba(0, 0, 0, 0.15),
      0 0 35px rgba(34, 197, 94, 0.2)
    `
  };

  const galleryImageStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'all 0.5s ease',
    filter: 'brightness(1) contrast(1.05) saturate(1.1)'
  };

  const galleryImageHoverStyles = {
    filter: 'brightness(0.8) contrast(1.15) saturate(1.2)',
    transform: 'scale(1.08)'
  };

  const galleryImageOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: `
      linear-gradient(135deg, 
        rgba(34, 197, 94, 0.1) 0%, 
        rgba(59, 130, 246, 0.08) 40%, 
        rgba(31, 41, 55, 0.7) 100%
      )
    `,
    opacity: '0',
    transition: 'all 0.5s ease',
    zIndex: '1'
  };

  const galleryImageOverlayHoverStyles = {
    opacity: '1'
  };

  const galleryImageInfoStyles = {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    padding: '30px 25px',
    zIndex: '2',
    transform: 'translateY(20px)',
    opacity: '0',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
  };

  const galleryImageInfoHoverStyles = {
    transform: 'translateY(0)',
    opacity: '1'
  };

  const galleryImageTitleStyles = {
    fontSize: '22px',
    fontWeight: '400',
    color: '#ffffff',
    marginBottom: '8px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px',
    lineHeight: '1.2'
  };

  const galleryImageLocationStyles = {
    fontSize: '12px',
    color: '#22c55e',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  // Tablet Styles
  const galleryTabletStyles = {
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleTabletStyles = {
    fontSize: '48px'
  };

  const gallerySubtitleTabletStyles = {
    fontSize: '18px'
  };

  const galleryGridTabletStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '25px'
  };

  const galleryImageItemTabletStyles = {
    height: '400px'
  };

  // Mobile Styles
  const galleryMobileStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const galleryTitleMobileStyles = {
    fontSize: '40px'
  };

  const gallerySubtitleMobileStyles = {
    fontSize: '16px'
  };

  const galleryGridMobileStyles = {
    gridTemplateColumns: '1fr',
    gap: '20px'
  };

  const galleryImageItemMobileStyles = {
    height: '340px'
  };

  const galleryImageInfoMobileStyles = {
    padding: '25px 20px'
  };

  const galleryImageTitleMobileStyles = {
    fontSize: '20px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(gallerySectionClass, gallerySectionStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryPatternOverlayClass, galleryPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryContainerClass, galleryContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryHeaderClass, galleryHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryTitleClass, galleryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(gallerySubtitleClass, gallerySubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryGridClass, galleryGridStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageItemClass, galleryImageItemStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageClass, galleryImageStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageOverlayClass, galleryImageOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageInfoClass, galleryImageInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageTitleClass, galleryImageTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(galleryImageLocationClass, galleryImageLocationStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageItemClass}--hover`, galleryImageItemHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageClass}--hover`, galleryImageHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageOverlayClass}--hover`, galleryImageOverlayHoverStyles);
    newRegistry[DEVICES.DESKTOP].set(`${galleryImageInfoClass}--hover`, galleryImageInfoHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(gallerySectionClass, galleryTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryTitleClass, galleryTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(gallerySubtitleClass, gallerySubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryGridClass, galleryGridTabletStyles);
    newRegistry[DEVICES.TABLET].set(galleryImageItemClass, galleryImageItemTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(gallerySectionClass, galleryMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryTitleClass, galleryTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(gallerySubtitleClass, gallerySubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryGridClass, galleryGridMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageItemClass, galleryImageItemMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageInfoClass, galleryImageInfoMobileStyles);
    newRegistry[DEVICES.MOBILE].set(galleryImageTitleClass, galleryImageTitleMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    gallerySectionClass,
    galleryPatternOverlayClass,
    galleryContainerClass,
    galleryHeaderClass,
    galleryTitleClass,
    gallerySubtitleClass,
    galleryGridClass,
    galleryImageItemClass,
    galleryImageClass,
    galleryImageOverlayClass,
    galleryImageInfoClass,
    galleryImageTitleClass,
    galleryImageLocationClass
  ]));

  // Nature landscape photography images data
  const natureImages = [
    {
      src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop&crop=center',
      title: 'Mountain Peak Sunrise',
      location: 'Rocky Mountains'
    },
    {
      src: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&h=400&fit=crop&crop=center',
      title: 'Forest Path Mystery',
      location: 'Pacific Northwest'
    },
    {
      src: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&h=400&fit=crop&crop=center',
      title: 'Serene Lake Reflection',
      location: 'Canadian Rockies'
    },
    {
      src: 'https://images.unsplash.com/photo-1506197603052-3cc9c3a201bd?w=600&h=400&fit=crop&crop=center',
      title: 'Ocean Waves at Sunset',
      location: 'California Coast'
    },
    {
      src: 'https://images.unsplash.com/photo-1518837695005-2083093ee35b?w=600&h=400&fit=crop&crop=center',
      title: 'Desert Sand Dunes',
      location: 'Sahara Desert'
    },
    {
      src: 'https://images.unsplash.com/photo-1447752875215-b2761acb3c5d?w=600&h=400&fit=crop&crop=center',
      title: 'Rolling Green Hills',
      location: 'Tuscany, Italy'
    },
    {
      src: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=600&h=400&fit=crop&crop=center',
      title: 'Misty Forest Morning',
      location: 'Olympic National Park'
    },
    {
      src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop&crop=center',
      title: 'Alpine Lake Tranquility',
      location: 'Swiss Alps'
    },
    {
      src: 'https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=600&h=400&fit=crop&crop=center',
      title: 'Autumn Forest Colors',
      location: 'New England'
    },
    {
      src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=400&fit=crop&crop=center',
      title: 'Dramatic Storm Clouds',
      location: 'Great Plains'
    },
    {
      src: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=600&h=400&fit=crop&crop=center',
      title: 'Wildflower Meadow',
      location: 'Colorado Rockies'
    },
    {
      src: 'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=600&h=400&fit=crop&crop=center',
      title: 'Coastal Cliff Views',
      location: 'Big Sur, California'
    }
  ];

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const gridId = Date.now() + 6;

  // Create image elements
  let currentId = gridId + 1;
  const imageElements = [];
  const imageIds = [];

  natureImages.forEach((image, index) => {
    const itemId = currentId++;
    const imageId = currentId++;
    const overlayId = currentId++;
    const infoId = currentId++;
    const titleId = currentId++;
    const locationId = currentId++;

    imageIds.push(itemId);

    imageElements.push(
      // Image Item Container
      {
        id: itemId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Nature Image Item ${index + 1}`,
        parentId: gridId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageItemClass,
        styles: {},
        children: [imageId, overlayId, infoId],
        text: '',
        attributes: {
          'data-element': 'nature-gallery-image-item',
          'data-index': index.toString()
        }
      },
      // Image
      {
        id: imageId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        name: `Nature Image ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('image'),
        className: galleryImageClass,
        styles: {},
        src: image.src,
        attributes: {
          'data-element': 'nature-gallery-image',
          'alt': image.title
        }
      },
      // Overlay
      {
        id: overlayId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Nature Image Overlay ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageOverlayClass,
        styles: {},
        children: [],
        text: '',
        attributes: {
          'data-element': 'nature-gallery-image-overlay'
        }
      },
      // Info
      {
        id: infoId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Nature Image Info ${index + 1}`,
        parentId: itemId,
        hierarchyName: getHierarchyName('div'),
        className: galleryImageInfoClass,
        styles: {},
        children: [titleId, locationId],
        text: '',
        attributes: {
          'data-element': 'nature-gallery-image-info'
        }
      },
      // Title
      {
        id: titleId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h4',
        name: `Nature Image Title ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('h4'),
        className: galleryImageTitleClass,
        styles: {},
        children: [],
        text: image.title,
        attributes: {
          'data-element': 'nature-gallery-image-title'
        }
      },
      // Location
      {
        id: locationId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Nature Image Location ${index + 1}`,
        parentId: infoId,
        hierarchyName: getHierarchyName('span'),
        className: galleryImageLocationClass,
        styles: {},
        children: [],
        text: image.location,
        attributes: {
          'data-element': 'nature-gallery-image-location'
        }
      }
    );
  });

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Nature Landscape Gallery Section',
      parentId: null,
      className: gallerySectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'nature-gallery-section',
        'data-component': 'section',
        'data-section-type': 'nature-gallery',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Nature Gallery Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'nature-gallery-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Nature Gallery Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: galleryContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'nature-gallery-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Nature Gallery Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'nature-gallery-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Nature Gallery Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: galleryTitleClass,
      styles: {},
      children: [],
      text: 'Nature & Landscape',
      attributes: {
        'data-element': 'nature-gallery-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Nature Gallery Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: gallerySubtitleClass,
      styles: {},
      children: [],
      text: 'Breathtaking landscapes and natural wonders captured in their purest form. From majestic mountains to serene forests, celebrating the beauty of our natural world.',
      attributes: {
        'data-element': 'nature-gallery-subtitle'
      }
    },
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Nature Gallery Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: galleryGridClass,
      styles: {},
      children: imageIds,
      text: '',
      attributes: {
        'data-element': 'nature-gallery-grid'
      }
    },
    ...imageElements
  ];

  return {
    natureGallerySection: newElements[0],
    elements: newElements
  };
}; 